/**
 * SamarbeidEkstern – offentlig (ingen pålogging) visning av en skapers
 * ledige datoer via en invitasjons-token. Oppfordrer eksterne til å lage
 * konto eller søke om skaper-status for full tilgang.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import CollabRequestComposer from "../components/collab/CollabRequestComposer";

export default function SamarbeidEkstern() {
  const { t } = useTranslation();
  const { token } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null); // { date, type } — ekstern pre-fyll av form

  const pickForExternal = (slot, type) => {
    setPickedSlot({ date: slot.date, type: (type || (slot.types || [])[0] || "").toLowerCase() });
    // Scroll til meldingsskjema
    setTimeout(() => {
      const el = document.querySelector('[data-testid="external-message-form"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  useEffect(() => {
    api.get(`/collab/invitations/view/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Invitasjonen finnes ikke eller er utløpt."));
  }, [token]);

  if (error) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="ext-error">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-4">Invitasjon ikke gyldig</h1>
          <p className="text-[var(--cream-soft)]">{error}</p>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return <Layout><div className="max-w-2xl mx-auto px-4 py-12 text-center text-[var(--cream-soft)]">Laster…</div></Layout>;
  }

  const { creator, slots, invitation_label } = data;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="ext-page">
        <div className="mb-8 text-center">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">
            INVITASJON{invitation_label ? ` – ${invitation_label.toUpperCase()}` : ""}
          </div>
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-1">
            {creator?.creator_display_name || creator?.username}
          </h1>
          <p className="text-sm text-[var(--cream-soft)]">
            har delt sin samarbeid-kalender med deg
          </p>
        </div>

        {creator?.contact_platform && creator?.contact_handle && (
          <div className="panel-tight p-4 mb-6 border-[var(--gold)]/40" data-testid="ext-contact">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--gold)] mb-1">KONTAKT MEG PÅ</div>
            {(() => {
              const plat = creator.contact_platform;
              const handle = creator.contact_handle;
              const isUrl = /^https?:\/\//i.test(handle);
              const platLabel = ({
                telegram: "Telegram", snapchat: "Snapchat", onlyfans: "OnlyFans", fansly: "Fansly",
                instagram: "Instagram", x: "X / Twitter", tiktok: "TikTok", reddit: "Reddit",
                bluesky: "Bluesky", kik: "Kik", discord: "Discord", other: "Annet",
              })[plat] || plat;
              const href = isUrl ? handle : (plat === "telegram" ? `https://t.me/${handle.replace(/^@/, "")}` : null);
              return (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-fraunces italic text-lg text-[var(--cream)]">{platLabel}:</span>
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                      className="font-manrope text-[var(--magenta)] hover:underline break-all">
                      {handle}
                    </a>
                  ) : (
                    <span className="font-manrope text-[var(--cream)] break-all">{handle}</span>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        <div className="panel-tight p-5 mb-6 bg-[var(--magenta)]/5 border-[var(--magenta)]/30">
          <h2 className="font-fraunces italic text-xl text-[var(--cream)] mb-3">{t("collab_ext_welcome.title")}</h2>
          <p className="text-sm text-[var(--cream-soft)] mb-3" dangerouslySetInnerHTML={{ __html: t("collab_ext_welcome.p1") }} />
          <p className="text-sm text-[var(--cream-soft)] mb-3" data-testid="ext-welcome-price" dangerouslySetInnerHTML={{ __html: t("collab_ext_welcome.p2") }} />
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/login" className="btn-magenta" data-testid="ext-cta-signup">{t("collab_ext_welcome.cta_signup")}</Link>
            <Link to="/bli-skaper" className="px-4 py-2 border border-[var(--gold)] text-[var(--gold)] font-meta hover:bg-[var(--gold)]/10"
              data-testid="ext-cta-creator">
              {t("collab_ext_welcome.cta_creator")}
            </Link>
          </div>
        </div>

        <h3 className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">
          LEDIGE DATOER ({slots.length})
        </h3>
        {slots.length === 0 ? (
          <div className="panel-tight p-6 text-center text-[var(--cream-soft)] font-fraunces italic">
            Ingen ledige datoer enda. Sjekk tilbake snart.
          </div>
        ) : (
          <ul className="space-y-2">
            {slots.map((s) => (
              <li key={s.date} className="panel-tight p-3" data-testid={`ext-slot-${s.date}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <strong className="text-[var(--cream)] font-fraunces text-lg">
                    {new Date(s.date).toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" })}
                  </strong>
                  <div className="flex flex-wrap gap-1">
                    {(s.types || []).map((tp) => (
                      <span key={tp} className="font-meta text-[10px] px-2 py-0.5 border border-[var(--magenta)]/40 text-[var(--magenta)]">
                        {tp.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
                {s.platforms?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {s.platforms.map((p) => (
                      <span key={p} className="font-meta text-[10px] text-[var(--cream-soft)]">{p.toUpperCase()}</span>
                    ))}
                  </div>
                )}
                {s.note && (
                  <p className="text-xs text-[var(--cream-soft)] mt-2 whitespace-pre-line">{s.note}</p>
                )}
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button onClick={() => pickForExternal(s)} type="button"
                    className="btn-ghost text-xs inline-flex items-center gap-1.5"
                    data-testid={`ext-pick-${s.date}`}>
                    Velg denne dagen
                  </button>
                  {user?.is_creator && (
                    <button onClick={() => setBookingSlot(s)} type="button"
                      className="btn-magenta text-xs inline-flex items-center gap-1.5"
                      data-testid={`ext-book-${s.date}`}>
                      Reserver som skaper
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <ExternalMessageForm token={token} slots={slots} picked={pickedSlot} onClearPick={() => setPickedSlot(null)} />

        {bookingSlot && (
          <CollabRequestComposer
            creator={creator}
            slot={bookingSlot}
            accounts={[]}
            onClose={() => setBookingSlot(null)}
            onSent={() => { setBookingSlot(null); toast.success("Forespørsel sendt."); }}
          />
        )}
      </div>
    </Layout>
  );
}

function ExternalMessageForm({ token, slots = [], picked = null, onClearPick }) {
  const [username, setUsername] = useState("");
  const [platform, setPlatform] = useState("snapchat");
  const [message, setMessage] = useState("");
  const [slotDate, setSlotDate] = useState("");
  const [slotType, setSlotType] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Synk pre-valg fra slot-knappen over
  useEffect(() => {
    if (picked?.date) {
      setSlotDate(picked.date);
      setSlotType((picked.type || "").toLowerCase());
    }
  }, [picked?.date, picked?.type]);

  // Finn tilgjengelige types for valgt dato
  const currentSlot = slots.find((s) => s.date === slotDate);
  const availableTypes = (currentSlot?.types || []).map((t) => t.toLowerCase());

  const clearSlot = () => {
    setSlotDate("");
    setSlotType("");
    if (onClearPick) onClearPick();
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" });
    } catch { return iso; }
  };

  const send = async () => {
    if (!username.trim() || !message.trim()) {
      toast.error("Fyll inn brukernavn og melding.");
      return;
    }
    // Hvis dato er valgt, må type også være valgt
    if (slotDate && !slotType) {
      toast.error("Velg også type samarbeid (SFS/MASS/…) for datoen.");
      return;
    }
    setSending(true);
    try {
      const payload = {
        username: username.trim(),
        platform,
        message: message.trim(),
      };
      if (slotDate && slotType) {
        payload.slot_date = slotDate;
        payload.slot_type = slotType;
      }
      await api.post(`/collab/invitations/${token}/message`, payload);
      setSent(true);
      toast.success("Melding sendt til skaperen.");
      setUsername(""); setMessage("");
      setSlotDate(""); setSlotType("");
      if (onClearPick) onClearPick();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="mt-8 panel-tight p-4 text-center">
        <div className="font-fraunces italic text-[var(--gold)] mb-1">Takk!</div>
        <p className="text-sm text-[var(--cream-soft)]">Skaperen har fått din henvendelse i sin innboks.</p>
        <button onClick={() => setSent(false)} className="font-meta text-xs text-[var(--magenta)] hover:underline mt-2">
          Send en til
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 panel-tight p-4" data-testid="external-message-form">
      <div className="font-fraunces italic text-lg text-[var(--cream)] mb-1">Send en henvendelse til skaperen</div>
      <p className="text-xs text-[var(--cream-soft)] mb-3">
        Du trenger ikke konto. Velg gjerne en konkret dag + type samarbeid, og skriv inn hvor skaperen når deg (plattform + brukernavn).
      </p>

      {/* Slot-valg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <select value={slotDate} onChange={(e) => { setSlotDate(e.target.value); setSlotType(""); }}
          className="input-editorial w-full" data-testid="ext-slot-date-select">
          <option value="">Velg dag (valgfritt)</option>
          {slots.map((s) => (
            <option key={s.date} value={s.date}>{formatDate(s.date)}</option>
          ))}
        </select>
        <select value={slotType} onChange={(e) => setSlotType(e.target.value)}
          disabled={!slotDate}
          className="input-editorial w-full disabled:opacity-40" data-testid="ext-slot-type-select">
          <option value="">{slotDate ? "Velg type samarbeid" : "Velg dag først"}</option>
          {availableTypes.map((tp) => (
            <option key={tp} value={tp}>{tp.toUpperCase()}</option>
          ))}
        </select>
      </div>
      {slotDate && slotType && (
        <div className="flex items-center gap-2 mb-3" data-testid="ext-slot-pill">
          <span className="font-meta text-[10px] px-2 py-1 border border-[var(--magenta)] text-[var(--magenta)] uppercase tracking-wider">
            {slotType.toUpperCase()} · {formatDate(slotDate)}
          </span>
          <button type="button" onClick={clearSlot}
            className="font-meta text-[10px] text-[var(--cream-soft)] hover:text-[var(--cream)] underline"
            data-testid="ext-slot-clear">
            Fjern valg
          </button>
        </div>
      )}

      {/* Kontakt-info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
          className="input-editorial w-full" data-testid="ext-msg-platform">
          <option value="snapchat">Snapchat</option>
          <option value="onlyfans">OnlyFans</option>
          <option value="fansly">Fansly</option>
          <option value="instagram">Instagram</option>
          <option value="telegram">Telegram</option>
          <option value="x">X / Twitter</option>
          <option value="tiktok">TikTok</option>
          <option value="reddit">Reddit</option>
          <option value="bluesky">Bluesky</option>
          <option value="other">Annet</option>
        </select>
        <input value={username} onChange={(e) => setUsername(e.target.value)} maxLength={80}
          placeholder="Brukernavn / handle"
          className="input-editorial w-full" data-testid="ext-msg-handle" />
      </div>
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={2000}
        placeholder="Hei! Vil gjerne booke denne dagen. Ta kontakt for detaljer."
        className="input-editorial w-full font-manrope text-sm mb-2" data-testid="ext-msg-text" />
      <button onClick={send} disabled={sending}
        className="btn-magenta w-full disabled:opacity-40" data-testid="ext-msg-send">
        {sending ? "Sender…" : "Send melding"}
      </button>
    </div>
  );
}
